import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../../branding/BrandingContext';
import { ATENDABOT_LOGO } from '../../branding/assets';
import { useAuthSession } from '../../contexts/AuthSessionContext';
import { login } from '../../services/api';
import { consumeForceLogin } from '../../services/session';
import { isInitialSetupPending } from '../../services/initialSetup';
import { acceptLogin } from '../../services/browserAuthRuntime';
import {
  getProductEntryPath,
  hasBillingAccess,
  hasProductAccess,
} from '../../utils/billing';
import { hasRestaurantAccess } from '../../utils/restaurant';
import SessionExpiryNotice from '../common/SessionExpiryNotice';
import PasswordRecoveryForm from './PasswordRecoveryForm';
import './LoginPage.css';

const getPostLoginPath = (session, branding) => {
  if (session) {
    if (!hasBillingAccess(session)) return '/billing';
    if (!hasRestaurantAccess(session)) return '/restaurante-inativo';
  }

  if (branding?.key !== 'atendabot' && isInitialSetupPending(session)) {
    return '/setup-inicial';
  }

  const productKey = branding?.productKey;
  const authenticatedPath = branding?.authenticatedPath;

  if (productKey && authenticatedPath) {
    const hasAccess = hasProductAccess(session, productKey, {
      allowTrialRestricted: true,
    });

    if (!hasAccess) {
      return branding.accessDeniedPath || authenticatedPath;
    }

    return getProductEntryPath(session, productKey, authenticatedPath);
  }

  return '/products';
};

const LoginPage = () => {
  const branding = useBranding();
  const { session, isAuthenticated, isHydrating } = useAuthSession();
  const brandName = branding.name || 'Gourmex';
  const isAtendabotBrand =
    branding?.loginVariant === 'atendabot' || branding?.key === 'atendabot';
  const heroHeading = isAtendabotBrand
    ? 'Atendimento automatizado com IA'
    : branding.copy?.heroHeading || `Escale seus resultados com a ${brandName}`;
  const logoPrimary = branding.logos?.primary;
  const description = isAtendabotBrand
    ? 'A inteligência artificial responde automaticamente, mantém o contexto e organiza cada atendimento.'
    : branding.copy?.description ||
      'Dados centralizados, processos automatizados e inteligência aplicada para decidir melhor no dia a dia do seu restaurante.';
  const isFoodBrand =
    branding?.landingVariant === 'foodstation' || branding?.key === 'foodstation';
  const loginBackground = isFoodBrand
    ? `${process.env.PUBLIC_URL}/login_food.jpeg`
    : `${process.env.PUBLIC_URL}/background_login.jpg`;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const forced = consumeForceLogin();
    if (forced) return;
    if (!isAuthenticated || isHydrating || !session) return;

    navigate(getPostLoginPath(session, branding), { replace: true });
  }, [branding, isAuthenticated, isHydrating, navigate, session]);

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const response = await login(username, password);

      if (response.data?.session) {
        const nextSession = acceptLogin(response.data);
        navigate(getPostLoginPath(nextSession, branding));
        return;
      }
      setErrorMessage('Erro inesperado. Tente novamente.');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setErrorMessage('Usuário ou senha inválidos.');
        return;
      }
      setErrorMessage('Erro ao realizar login. Verifique sua conexão.');
    }
  };

  return (
    <div className={`login-page${isAtendabotBrand ? ' login-page--atendabot' : ''}`}>
      <SessionExpiryNotice />
      <div className="login-shell">
        <div
          className="login-hero"
          style={isAtendabotBrand ? undefined : { '--login-hero-bg': `url(${loginBackground})` }}
        >
          <div className="login-hero__overlay" />
          <div className="login-hero__content">
            <div className="login-hero__text">
              {isAtendabotBrand && (
                <span className="atendabot-login-eyebrow">IA para atendimento</span>
              )}
              <h1 className="hero-heading">{heroHeading}</h1>
              <p className="hero-subtext">{description}</p>
            </div>
          </div>
        </div>

        <div className="login-form-pane">
          {isAtendabotBrand && (
            <a className="atendabot-login-back" href="/">
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M19 12H5m6 6-6-6 6-6" />
              </svg>
              Voltar para o início
            </a>
          )}
          <div className="login-form__logo">
            {isAtendabotBrand ? (
              <div className="atendabot-login-logo" role="img" aria-label={brandName}>
                <span className="atendabot-login-logo__mark" aria-hidden="true">
                  <img src={ATENDABOT_LOGO} alt="" />
                </span>
                <span className="atendabot-login-logo__name" aria-hidden="true">
                  atenda<strong>bot</strong>
                </span>
              </div>
            ) : logoPrimary ? (
              <img src={logoPrimary} alt={brandName} />
            ) : (
              <span className="login-form__logo-text">{brandName}</span>
            )}
          </div>
          <div className="login-form__header">
            <h1>{recovering ? 'Recuperar senha' : isAtendabotBrand ? `Bem-vindo ao ${brandName}` : 'Bem-vindo de volta'}</h1>
            {isAtendabotBrand && !recovering && (
              <p>Entre para continuar no seu painel de atendimento.</p>
            )}
          </div>

          {recovering ? (
            <PasswordRecoveryForm initialEmail={username} onBack={() => setRecovering(false)}
              onSuccess={(message) => {
                setPassword('');
                setErrorMessage('');
                setSuccessMessage(message);
                setRecovering(false);
              }} />
          ) : (
          <form className="login-form" onSubmit={handleLogin}>
            {successMessage && <p className="password-recovery__notice" role="status">{successMessage}</p>}
            <label className="input-label" htmlFor="username">
              Usuário/E-mail
            </label>
            <div className="login-input-wrap">
              {isAtendabotBrand && (
                <svg className="atendabot-login-input-icon" aria-hidden="true" viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21a8 8 0 0 1 16 0" />
                </svg>
              )}
              <input
                id="username"
                type="text"
                autoComplete="username"
                placeholder="Digite seu usuário ou e-mail"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <label className="input-label" htmlFor="password">
              Senha
            </label>
            <div className="login-input-wrap">
              {isAtendabotBrand && (
                <svg className="atendabot-login-input-icon" aria-hidden="true" viewBox="0 0 24 24">
                  <rect x="4" y="10" width="16" height="11" rx="3" />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                </svg>
              )}
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="Digite sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button type="button" className="login-text-button login-forgot-password" onClick={() => {
              setPassword('');
              setErrorMessage('');
              setSuccessMessage('');
              setRecovering(true);
            }}>Esqueci minha senha</button>
            {errorMessage && (
              <p className="error-message" role="alert" aria-live="polite">
                {errorMessage}
              </p>
            )}
            <button type="submit" className="login-button">
              <span>Entrar</span>
              {isAtendabotBrand && (
                <svg aria-hidden="true" viewBox="0 0 24 24">
                  <path d="M5 12h14m-6-6 6 6-6 6" />
                </svg>
              )}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
